import './HwilComponentSelectionModal.scss'
import { useMemo } from 'react'
import {
    FooterAction,
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '../../../../../CommonComponents/modals/Modal'
import useStaticData from '../../../hooks/useStaticData'
import useModal from '../../../hooks/useModal'
import useForm from '../../../hooks/useForm'
import { CloseSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { preventEnterKey } from '../../../../../../utils/commonFunctions'
import { Form } from 'react-bootstrap'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { isEmpty } from 'lodash'
import { RemoteAppDataType, HwilComponentType, RemoteAppConfiguration } from '../../../../types'
import useScenarioResponse from '../../../hooks/useScenarioResponse'

const NONE_COMPONENT = {
    uniqueId: 'none',
    name: 'None',
    category: 'none',
}

const HwilComponentSelectionModal = () => {

    const { setFormData } = useForm()
    const { modals, setModals } = useModal()
    const { allHwilComponents } = useStaticData()
    const { scenarioResponse } = useScenarioResponse()

    const allPayloads: RemoteAppDataType[] = useMemo(() => modals?.configured?.data?.groupPayloads || [], [modals?.configured?.data?.groupPayloads])
    const selectedPayload: HwilComponentType[] = useMemo(() => modals?.configured?.data?.payload || [], [modals?.configured?.data?.payload])
    const selectedBus: HwilComponentType[] = useMemo(() => modals?.configured?.data?.bus || [], [modals?.configured?.data?.bus])

    const payloads = useMemo(() => [NONE_COMPONENT, ...allHwilComponents.payload], [allHwilComponents?.payload])
    const bus = useMemo(() => [NONE_COMPONENT, ...allHwilComponents.bus], [allHwilComponents?.bus])

    const open = useMemo(() => modals?.configured?.name === 'hwilComponent', [modals?.configured?.name])

    const onClose = () => {
        setModals((prev) => {
            return {
                ...prev,
                configured: null
            }
        })
    }

    const getRemoteAppConfiguration = () => {
        const remoteAppConfiguration: RemoteAppConfiguration = {
            remoteApp: { AppData: [] },
            remoteBus: {}
        };
        const payloadMap = new Map(allPayloads.map(p => [p.id, p]));
        selectedPayload.forEach((component: HwilComponentType) => {
            const payload = payloadMap.get(component.uniqueId);
            if (!payload) return;
            const existingAppData = scenarioResponse?.remoteAppConfiguration.remoteApp.AppData || [];
            const matchedApp = existingAppData.find(app => app.id === payload.id);
            remoteAppConfiguration.remoteApp.AppData.push(
                matchedApp ? { ...payload, ...matchedApp } : payload
            );
        });
        return remoteAppConfiguration;
    };

    const onSave = () => {
        setFormData(prev => ({
            ...prev,
            hwil: {
                hwInfo: [...selectedPayload, ...selectedBus],
                remoteAppConfiguration: getRemoteAppConfiguration()
            }
        }));
        onClose();
    };


    const selectComponent = (compType: 'payload' | 'bus', component: HwilComponentType) => {
        setModals((prev) => ({
            ...prev!,
            configured: {
                ...prev?.configured!,
                data: {
                    ...prev?.configured?.data!,
                    [compType]: component.category === 'none' ? [] : [component]
                }
            }
        }))
    }

    if (!open) return null
    return (
        <Modal open={open} pinned={true} disabled={true} className={`scenario__hwil__selection__modal`}>
            <ModalHeader>
                <HeaderTitleContainer>
                    <HeaderTitle>
                        Component
                    </HeaderTitle>
                </HeaderTitleContainer>
                <HeaderActionsContainer>
                    <HeaderAction title='Close' onClick={onClose}>
                        <CloseSvg />
                    </HeaderAction>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody>
                <div className='hwil__selection__container__body'>
                    <div className='hwil__selection__container__body__section'>
                        <span className='body__section__header__text'>Payload</span>
                        <div className='body__section__list'>
                            {payloads.map(payload => {
                                const checked = isEmpty(selectedPayload) ? payload.uniqueId === 'none' : selectedPayload.find(pay => pay.uniqueId === payload.uniqueId)
                                const show_badge = payload?.uniqueId !== 'none' && checked
                                return (
                                    <button className={`body__section__list__item ${checked ? 'selected' : ''}`} key={payload?.uniqueId}
                                        onClick={() => selectComponent('payload', payload)}
                                    >
                                        <div className='item__checkbox__container'>
                                            <Form.Check
                                                type='radio'
                                                label=""
                                                className="custom__checkbox__selection"
                                                checked={checked ? true : false}
                                                onKeyDown={preventEnterKey}
                                            />
                                        </div>
                                        <span className='item__text__container'>
                                            <CustomToolTip title={payload?.name} placement='top-start'>
                                                {payload?.name}
                                            </CustomToolTip>
                                        </span>
                                        {show_badge && <span className='item__badge'>HWIL</span>}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    <div className='body__section__devider' />
                    <div className='hwil__selection__container__body__section'>
                        <span className='body__section__header__text'>Bus Components</span>
                        <div className='body__section__list'>
                            {bus.map(buscomp => {
                                const checked = isEmpty(selectedBus) ? buscomp.uniqueId === 'none' : selectedBus.find(bus => bus.uniqueId === buscomp.uniqueId)
                                const show_badge = buscomp?.uniqueId !== 'none' && checked
                                return (
                                    <button className={`body__section__list__item ${checked ? 'selected' : ''}`} key={buscomp?.uniqueId}
                                        onClick={() => selectComponent('bus', buscomp)}
                                    >
                                        <div className='item__checkbox__container'>
                                            <Form.Check
                                                type='radio'
                                                label=""
                                                className="custom__checkbox__selection"
                                                checked={checked ? true : false}
                                                onKeyDown={preventEnterKey}
                                            />
                                        </div>
                                        <span className='item__text__container'>
                                            <CustomToolTip title={buscomp?.name} placement='top-start'>
                                                {buscomp?.name}
                                            </CustomToolTip>
                                        </span>
                                        {show_badge && <span className='item__badge'>HWIL</span>}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <FooterAction variant='outline__button'
                    onClick={onClose}
                >
                    Cancel
                </FooterAction>
                <FooterAction variant='primary__button'
                    disabled={isEmpty(selectedPayload) && isEmpty(selectedBus)}
                    onClick={onSave}
                >
                    Save
                </FooterAction>
            </ModalFooter>
        </Modal>
    )
}

export default HwilComponentSelectionModal